import clsx from 'clsx';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';

import { setCustomParticipantVisible } from '../../../base/conference';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { VIDEO_TYPE } from '../../../base/media';
import { getLocalParticipant, getPinnedParticipant, pinParticipant } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { updateSettings } from '../../../base/settings';
import { exitTileViewIfNeeded } from '../../../conference/filmStripViewManager';
import { LAYOUTS, VideoLayout } from '../../../video-layout';
import { getLocalVideoZoom } from '../../../zoom-sync';
import {
    handleThumbnailClick,
    hideActiveDoubleClickTooltip,
    updateThumbnailZoomTransform
} from '../../functions.web';

import { LocalVideoContextMenu, showLocalVideoContextMenu } from './LocalVideoContextMenu';
import Thumbnail from './Thumbnail';

/**
 * The ID of the local thumbnail container.
 */
const VIDEO_SPAN_ID = 'localVideoContainer';

type Props = {

    /**
     * Whether local video flipping is disabled in config.
     */
    _disableLocalVideoFlip: boolean,

    /**
     * The current filmstrip view.
     */
    _filmStripView?: string,

    /**
     * Whether audio-only mode is active.
     */
    _isAudioOnly: boolean,

    /**
     * Whether picture-in-picture mode is enabled.
     */
    _isPiP: boolean,

    /**
     * Whether the local video is flipped horizontally.
     */
    _localFlipX: boolean,

    /**
     * The local participant object from redux.
     */
    _localParticipant?: Object,

    /**
     * The object fit used for the video in tile view.
     */
    _objectFit: string,

    /**
     * The pinned participant from redux.
     */
    _pinnedParticipant?: Object,

    /**
     * The synchronized zoom of the local camera.
     */
    _zoomState?: Object,

    /**
     * Additional CSS class name.
     */
    className?: string,

    /**
     * The redux dispatch function.
     */
    dispatch: Function,

    /**
     * Whether the thumbnail is hovered (controlled).
     */
    isHovered?: boolean,

    /**
     * Custom inline styles.
     */
    style?: Object
};

type State = {
    isHovered: boolean
};

/**
 * React component representing the local participant thumbnail.
 */
class LocalVideo extends Component<Props, State> {
    _containerRef: Object;
    _firstClick: ?number;

    /**
     * Initializes a new LocalVideo component instance.
     *
     * @param {Props} props - The component props.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            isHovered: false
        };

        this._containerRef = React.createRef();

        this._onMouseEnter = this._onMouseEnter.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
        this._onContextMenu = this._onContextMenu.bind(this);
        this._onClick = this._onClick.bind(this);
        this._setFirstClick = this._setFirstClick.bind(this);
        this.setFlipX = this.setFlipX.bind(this);
        this.togglePin = this.togglePin.bind(this);
        this.updateZoomTransform = this.updateZoomTransform.bind(this);
    }

    /**
     * Registers the thumbnail with VideoLayout and syncs zoom transform.
     *
     * @inheritdoc
     */
    componentDidMount() {
        VideoLayout.setLocalVideo?.(this);
        this.updateZoomTransform();
    }

    /**
     * Re-applies zoom when it or the mirroring changes.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (prevProps._localFlipX !== this.props._localFlipX
                || prevProps._zoomState !== this.props._zoomState
                || prevProps._filmStripView !== this.props._filmStripView
                || prevProps._objectFit !== this.props._objectFit) {
            this.updateZoomTransform();
        }
    }

    /**
     * Unregisters the thumbnail and clears the double click tooltip.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._clearDoubleClickTimeout();
        if (VideoLayout.getLocalVideo?.() === this) {
            VideoLayout.setLocalVideo?.(null);
        }
    }

    /**
     * Returns the local participant ID; used by VideoLayout.
     *
     * @returns {string}
     */
    getId(): string {
        return this.props._localParticipant?.id || 'local';
    }

    /**
     * Stores the mirroring of the local video in settings.
     *
     * @param {boolean} val - True if flipped.
     * @returns {void}
     */
    setFlipX(val: boolean) {
        this.props.dispatch(updateSettings({ localFlipX: val }));
    }

    /**
     * Pins the local participant, or unpins if already pinned; used by VideoLayout.
     *
     * @returns {void}
     */
    togglePin(): void {
        const id = this.getId();
        const { dispatch, _filmStripView, _pinnedParticipant } = this.props;

        exitTileViewIfNeeded(dispatch, _filmStripView);
        dispatch(pinParticipant(_pinnedParticipant?.id === id ? null : id));
        dispatch(setCustomParticipantVisible(false));
    }

    /**
     * Applies the synchronized zoom to the thumbnail video.
     *
     * @returns {void}
     */
    updateZoomTransform(): void {
        updateThumbnailZoomTransform({
            container: this._containerRef.current,
            isLocal: true,
            localFlipX: this.props._localFlipX,
            objectFit: this._getObjectFit(),
            zoomState: this.props._zoomState
        });
    }

    /**
     * Returns the object fit of the video for the current view.
     *
     * @private
     * @returns {string}
     */
    _getObjectFit() {
        return this.props._filmStripView === LAYOUTS.TILE_VIEW ? this.props._objectFit : 'cover';
    }

    /**
     * Handles mouse enter event.
     *
     * @private
     * @returns {void}
     */
    _onMouseEnter() {
        this.setState({ isHovered: true });
    }

    /**
     * Handles mouse leave event.
     *
     * @private
     * @returns {void}
     */
    _onMouseLeave() {
        this.setState({ isHovered: false });
        this._clearDoubleClickTimeout();
    }

    /**
     * Clears any active double click timer and tooltip.
     *
     * @private
     * @returns {void}
     */
    _clearDoubleClickTimeout() {
        if (this._firstClick) {
            window.clearTimeout(this._firstClick);
            this._firstClick = undefined;
        }
        hideActiveDoubleClickTooltip();
    }

    /**
     * Stores the double click timer.
     *
     * @param {?number} timer - The timer.
     * @private
     * @returns {void}
     */
    _setFirstClick(timer) {
        this._firstClick = timer;
    }

    /**
     * Handles context menu event.
     *
     * @param {React.MouseEvent} event - The context menu event.
     * @private
     * @returns {void}
     */
    _onContextMenu(event: React.MouseEvent) {
        if (!this.props._disableLocalVideoFlip) {
            showLocalVideoContextMenu(event.nativeEvent, VIDEO_SPAN_ID);
        }
    }

    /**
     * Handles click event for pinning/unpinning.
     *
     * @param {React.MouseEvent} event - The click event.
     * @private
     * @returns {void}
     */
    _onClick(event: React.MouseEvent) {
        handleThumbnailClick({
            container: this._containerRef.current,
            dispatch: this.props.dispatch,
            event,
            filmStripView: this.props._filmStripView,
            firstClickTimer: this._firstClick,
            id: this.getId(),
            isPiP: this.props._isPiP,
            pinnedParticipant: this.props._pinnedParticipant,
            setFirstClickTimer: this._setFirstClick
        });
    }

    /**
     * Implements React's Component.render().
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const id = this.getId();
        const {
            _disableLocalVideoFlip,
            _isAudioOnly,
            _localParticipant,
            className,
            isHovered: controlledHover,
            style
        } = this.props;

        const isHovered = typeof controlledHover === 'boolean' ? controlledHover : this.state.isHovered;
        const isPinned = Boolean(_localParticipant?.pinned || _localParticipant?.moderatorPin);
        const videoObjectFit = this._getObjectFit();
        const portalContainer = document.getElementById('context-menu-portal');
        const contextMenu = !_disableLocalVideoFlip && (
            <LocalVideoContextMenu
                participantId = { id }
                setFlipX = { this.setFlipX }
                showParticipantName = { true }
                videoSpanId = { VIDEO_SPAN_ID } />
        );

        return (
            <span
                className = { clsx(
                    'videocontainer',
                    `video-object-fit-${videoObjectFit}`,
                    _isAudioOnly && 'audio-only',
                    isPinned && 'videoContainerFocused',
                    className
                ) }
                id = { VIDEO_SPAN_ID }
                onClick = { this._onClick }
                onContextMenu = { this._onContextMenu }
                onMouseEnter = { this._onMouseEnter }
                onMouseLeave = { this._onMouseLeave }
                ref = { this._containerRef }
                style = {{
                    objectFit: videoObjectFit,
                    ...style
                }}>
                <Thumbnail
                    isHovered = { isHovered }
                    participantID = { id } />
                {contextMenu && portalContainer ? createPortal(contextMenu, portalContainer) : contextMenu}
            </span>
        );
    }
}

/**
 * Maps (parts of) the redux state to the associated LocalVideo props.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const conference = state['features/base/conference'];

    return {
        _disableLocalVideoFlip: Boolean(state['features/base/config']?.disableLocalVideoFlip),
        _filmStripView: conference?.filmStripView,
        _isAudioOnly: Boolean(conference?.audioOnly),
        _isPiP: Boolean(getPictureInPictureModeConfig(state)),
        _localFlipX: Boolean(state['features/base/settings']?.localFlipX),
        _localParticipant: getLocalParticipant(state),
        _objectFit: conference?.objectFit || 'cover',
        _pinnedParticipant: getPinnedParticipant(state),
        _zoomState: getLocalVideoZoom(state, VIDEO_TYPE.CAMERA)
    };
}

export default connect(_mapStateToProps)(LocalVideo);
